import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { saveData } from 'services/api/saveData';
import { useTimer } from 'services/timer/TimerContext';
import DeleteModal from 'components/modals/DeleteModal';
import LinkItemModal from 'components/modals/LinkItemModal';
import ContactForm from '../contacts/ContactForm';
import ProposalModal from '../proposals/ProposalModal';
import JobModal from '../jobs/JobModal';
import TimerStartForm from '../timesheets/TimerStartForm';
import { Ticket } from './TicketTypes';

interface TicketActionsOptions {
    ticketId: number | null;
    ticket?: Ticket | null; // The loaded ticket, needed for the create and timer actions
    onDeleted: () => void;
    onChanged: () => void;
}

// The actions of a ticket, deleting it, switching its requester, creating a proposal, job or deal from it and starting a timer, following the rights of the user
export const useTicketActions = ({ ticketId, ticket, onDeleted, onChanged }: TicketActionsOptions) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { setFloatingAlert } = useGlobalContext();
    const { activeFeatures } = useAuthContext();
    const { initializeModal, closeAllModals } = useModal();
    const { runningTimer } = useTimer();
    const canDelete = useAllowedRight('can_delete');
    const onlyViewRights = useAllowedRight('only_view');

    // The contact and contact person of the requester, an organization requester has no person
    const getRequesterContacts = () => {
        const reporter = ticket?.reporter_contact ?? null;
        const organization = reporter?.entity_type === 'organization' ? reporter : ticket?.reporter_organization ?? null;
        const person = reporter && reporter.entity_type !== 'organization' ? reporter : null;
        return {
            contact: organization?.id ?? person?.id ?? null,
            contactPerson: organization && person ? person.id : null,
        };
    };

    // Create a proposal from the ticket and open it in its modal
    const handleCreateProposal = async () => {
        if (!ticket) return;
        const { contact, contactPerson } = getRequesterContacts();
        try {
            const response = await saveData({ apiUrl: 'post_proposal', method: 'post', data: {
                recipient: contact, recipient_person: contactPerson, ticket: ticket.id, division: ticket.division
            } });
            if (response?.status === 201) {
                onChanged();
                initializeModal(<ProposalModal itemId={response.data.id} />, { itemId: response.data.id, modalSize: 'large' });
            }
        } catch (error) {
            setFloatingAlert({ type: 'danger', message: 'ticket.composer.save_failed_message' });
        }
    };

    // Create a job from the ticket and open it in its modal
    const handleCreateJob = async () => {
        if (!ticket) return;
        const { contact, contactPerson } = getRequesterContacts();
        try {
            const response = await saveData({ apiUrl: 'post_job', method: 'post', data: {
                client: contact, reporting_person: contactPerson, ticket: ticket.id, division: ticket.division,
                subject: ticket.subject, plan_method: 'add_to_storage'
            } });
            if (response?.status === 201) {
                onChanged();
                initializeModal(<JobModal itemId={response.data.id} />, { itemId: response.data.id, modalSize: 'large' });
            }
        } catch (error) {
            setFloatingAlert({ type: 'danger', message: 'ticket.composer.save_failed_message' });
        }
    };

    // Create a deal from the ticket and open its page
    const handleCreateDeal = async () => {
        if (!ticket) return;
        const { contact, contactPerson } = getRequesterContacts();
        if (!contact) {
            setFloatingAlert({ type: 'danger', message: 'ticket.actions.deal_requires_requester_message' });
            return;
        }
        try {
            const response = await saveData({ apiUrl: 'post_deal', method: 'post', data: {
                contact, contact_person: contactPerson, title: ticket.subject || t('ticket.general.ticket_title', { number: ticket.number }),
                ticket: ticket.id, division: ticket.division
            } });
            if (response?.status === 201) {
                closeAllModals();
                history.push(`/deals/${response.data.id}`);
            }
        } catch (error) {
            setFloatingAlert({ type: 'danger', message: 'ticket.composer.save_failed_message' });
        }
    };

    // Open the start form of a timer linked to the ticket
    const handleStartTimer = () => {
        if (!ticket) return;
        if (runningTimer) {
            setFloatingAlert({ type: 'danger', message: 'timerow.timer.already_running_error' });
            return;
        }
        initializeModal(<TimerStartForm ticketId={ticket.id} onStarted={onChanged} />, {
            title: t('timerow.timer.start_title'),
            modalSize: 'add-form-smaller'
        });
    };

    // Open the delete confirmation and delete the ticket
    const handleDelete = () => {
        initializeModal(
            <DeleteModal onConfirmDelete={async () => {
                try {
                    await saveData({ apiUrl: `delete_ticket/${ticketId}`, method: 'patch', data: {} });
                    onDeleted();
                } catch (error) {
                    setFloatingAlert({ type: 'danger', message: 'ticket.composer.save_failed_message' });
                }
            }} />,
            { modalSize: 'extra-small' }
        );
    };

    // Open the modal to search another contact as requester of the ticket
    const handleSwitchReporter = () => {
        initializeModal(
            <LinkItemModal
                itemId={ticketId!}
                searchFieldConfig={{
                    name: 'contact',
                    placeholder: 'contact.general.link_contact_placeholder',
                    objectName: 'contact.general.object_name.singular',
                    endpoint: 'get_contact_search',
                    addNewItem: { modalForm: <ContactForm /> }
                }}
                submitConfig={{ endpoint: 'patch_ticket', apiField: 'reporter' }}
                refetchData={onChanged}
            />,
            { title: t('ticket.general.switch_requester_label'), modalSize: 'small' }
        );
    };

    // The create and timer actions need a loaded ticket, edit rights and the active feature
    const canAct = !!ticket && !onlyViewRights;

    return {
        onDelete: canDelete ? handleDelete : undefined,
        onSwitchReporter: onlyViewRights ? undefined : handleSwitchReporter,
        onCreateProposal: canAct && activeFeatures.includes('proposals') ? handleCreateProposal : undefined,
        onCreateJob: canAct && activeFeatures.includes('jobs') ? handleCreateJob : undefined,
        onCreateDeal: canAct && activeFeatures.includes('deals') ? handleCreateDeal : undefined,
        onStartTimer: canAct && activeFeatures.includes('manage_timesheets') ? handleStartTimer : undefined,
    };
};
